//Angular imports
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

//RxJS imports
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

//3rd party imports
import { plainToInstance } from 'class-transformer';

//Service imports
import { HelperService } from '../../../_services/shared-services/helper.service';
import { RPCacheRegistrationService } from '../../../_services/shared-services/rp-cache-registration-service';

//Model imports
import { AccResultDto } from '../../_models/architectural-application-result-dto';
import { AccParameterDto } from '../../_models/architectural-application-parameter-dto';
import { BaseResidentParameterDto } from '../../../_models/base-resident-parameter-dto';
import { AccFormsUrlResultDto } from '../../_models/architectural-application-forms-url-result-dto';
import { AccDocumentsParameterDto } from '../../_models/architecutral-application-documents-parameter-dto';
import { AccDocumentsResultDto } from '../../_models/architectural-application-documents-result-dto';
import { AccCategoryResultDto } from '../../_models/architectural-application-category-result-dto';
import { ResultStatusDto } from '../../../_models/result-status-dto';
import { SubmitAccParameterDto } from '../../_models/architechtural-application-submit-parameter-dto';
import { AccAcknowledgementResultDto } from '../../_models/architectural-application-acknowledgement-result-dto';

//Service imports
import { EnvironmentConfigService } from '../../../_services/environment-config.service';

//Constant imports
import { CacheDuration } from '../../../_constants/cache-duration';

@Injectable({
  providedIn: 'root'
})
export class ArchitecturalApplicationService {
  constructor(
    private httpClient: HttpClient,
    private helperService: HelperService,
    private cacheRegistrationService: RPCacheRegistrationService,
    private envConfig: EnvironmentConfigService
  ) {}

  getAccList(params: AccParameterDto): Observable<AccResultDto[]> {
    let queryParams: string = params.getQueryString();
    if (!!params.beginDate) {
      queryParams += `&beginDate=${this.helperService.getISODateString(params.beginDate)}`;
    }
    if (!!params.endDate) {
      queryParams += `&endDate=${this.helperService.getISODateString(params.endDate)}`;
    }

    const url =
      this.envConfig.ccResidentPortalApiUrl + 'account/acc/' + queryParams;
    this.cacheRegistrationService.registerForCache(url, params.clearCache);
    const headers = this.cacheRegistrationService.getHttpRequestHeaders(
      url,
      CacheDuration.fifteenMinute
    );
    const options = { headers: headers };

    return this.httpClient
      .get<AccResultDto[]>(url, options)
      .pipe(
        map((responseData: any[]) =>
          plainToInstance(AccResultDto, responseData)
        )
      );
  }

  getGuidelineAndFormDownloadLink(
    params: BaseResidentParameterDto
  ): Observable<AccFormsUrlResultDto> {
    const url =
      this.envConfig.ccResidentPortalApiUrl +
      'account/acc/forms' +
      params.getQueryString();
    this.cacheRegistrationService.registerForCache(url, params.clearCache);
    const headers = this.cacheRegistrationService.getHttpRequestHeaders(
      url,
      CacheDuration.fifteenMinute
    );
    const options = { headers: headers };

    return this.httpClient
      .get<AccFormsUrlResultDto>(url, options)
      .pipe(
        map((responseData: any) =>
          plainToInstance(AccFormsUrlResultDto, responseData)
        )
      );
  }

  getFileFromUrl(url: string): Observable<Blob> {
    return this.httpClient
      .get(url, { responseType: 'arraybuffer' })
      .pipe(map((responseData: any) => new Blob([responseData])));
  }

  getDocumentsList(
    params: AccDocumentsParameterDto
  ): Observable<AccDocumentsResultDto[]> {
    const url =
      this.envConfig.ccResidentPortalApiUrl +
      'account/acc/doc/list' +
      params.getQueryString();
    this.cacheRegistrationService.registerForCache(url, params.clearCache);
    const headers = this.cacheRegistrationService.getHttpRequestHeaders(
      url,
      CacheDuration.fifteenMinute
    );
    const options = { headers: headers };
    return this.httpClient
      .get<AccDocumentsResultDto[]>(url, options)
      .pipe(
        map((responseData: any[]) =>
          plainToInstance(AccDocumentsResultDto, responseData)
        )
      );
  }

  getImprovementRequestCategories(
    params: BaseResidentParameterDto
  ): Observable<AccCategoryResultDto[]> {
    const url =
      this.envConfig.ccResidentPortalApiUrl +
      'account/acc/categories' +
      params.getQueryString();
    this.cacheRegistrationService.registerForCache(url, params.clearCache);
    const headers = this.cacheRegistrationService.getHttpRequestHeaders(
      url,
      CacheDuration.fifteenMinute
    );
    const options = { headers: headers };
    return this.httpClient
      .get<AccCategoryResultDto[]>(url, options)
      .pipe(
        map((responseData: any[]) =>
          plainToInstance(AccCategoryResultDto, responseData)
        )
      );
  }

  submitNewImprovementRequest(
    data: SubmitAccParameterDto
  ): Observable<ResultStatusDto> {
    return this.httpClient
      .post<ResultStatusDto>(
        this.envConfig.ccResidentPortalApiUrl + 'account/acc/submit',
        data
      )
      .pipe(
        map((responseData: any) =>
          plainToInstance(ResultStatusDto, responseData)
        )
      );
  }

  getAcknowledgementText(
    params: BaseResidentParameterDto
  ): Observable<AccAcknowledgementResultDto> {
    const url =
      this.envConfig.ccResidentPortalApiUrl +
      'account/acc/acknowledge' +
      params.getQueryString();
    this.cacheRegistrationService.registerForCache(url, params.clearCache);
    const headers = this.cacheRegistrationService.getHttpRequestHeaders(
      url,
      CacheDuration.fifteenMinute
    );
    const options = { headers: headers };
    return this.httpClient
      .get(url, options)
      .pipe(
        map((responseData: any) =>
          plainToInstance(AccAcknowledgementResultDto, responseData)
        )
      );
  }
}
